import React, { useEffect, useMemo, useState } from 'react';
import { HoverInsight } from '../types';
import MetricCard from '../components/MetricCard';
import SheetDataViewer from '../components/SheetDataViewer';
import RecentChangesPanel from '../components/RecentChangesPanel';
import { FileSpreadsheet } from 'lucide-react';
import api from '../lib/api';
import { formatIST } from '../lib/formatDate';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip, ResponsiveContainer, Cell
} from 'recharts';

interface DesignTrackerProps {
  onHover: (insight: HoverInsight | null) => void;
}

const colIdx = (header: string[], name: string) => header.findIndex((h) => (h || '').toLowerCase().trim() === name.toLowerCase());
const COLORS = ['#1d4ed8', '#0ea5e9', '#60a5fa', '#0369a1', '#38bdf8', '#93c5fd'];

export default function DesignTracker({ onHover }: DesignTrackerProps) {
  const [tabs, setTabs] = useState<Record<string, string[][]>>({});
  const [connected, setConnected] = useState(false);
  const [syncedAt, setSyncedAt] = useState<string | null>(null);

  useEffect(() => {
    api.get('/design-tracker').then((res) => { setTabs(res.data.tabs || {}); setConnected(res.data.connected); setSyncedAt(res.data.synced_at); }).catch(() => setConnected(false));
  }, []);

  // All figures below are derived directly from the live "Product Plan" tab — real rows,
  // real column values, no fabricated designers/SKUs/dates.
  const rows = tabs['Product Plan'] || [];
  const header = rows[0] || [];
  const dataRows = rows.slice(1).filter((r) => r.some((c) => c));
  const leadIdx = colIdx(header, 'Lead');
  const quarterIdx = colIdx(header, 'Launch Quarter');
  const skuIdx = colIdx(header, 'No. of SKUs');

  const leadBreakdown = useMemo(() => {
    const m: Record<string, number> = {};
    dataRows.forEach((r) => { const lead = leadIdx >= 0 ? (r[leadIdx] || 'Unassigned') : 'Unassigned'; m[lead] = (m[lead] || 0) + 1; });
    return Object.entries(m).sort((a, b) => b[1] - a[1]).map(([name, value]) => ({ name, value }));
  }, [dataRows, leadIdx]);

  const quarterBreakdown = useMemo(() => {
    const m: Record<string, number> = {};
    dataRows.forEach((r) => { const q = quarterIdx >= 0 ? (r[quarterIdx] || 'Unscheduled') : 'Unscheduled'; m[q] = (m[q] || 0) + 1; });
    return Object.entries(m).sort((a, b) => a[0].localeCompare(b[0])).map(([name, value]) => ({ name, value }));
  }, [dataRows, quarterIdx]);

  const totalSkus = useMemo(() => {
    if (skuIdx < 0) return null;
    let sum = 0;
    dataRows.forEach((r) => { const n = parseFloat((r[skuIdx] || '').toString().replace(/[^0-9.]/g, '')); if (!isNaN(n)) sum += n; });
    return sum;
  }, [dataRows, skuIdx]);

  const topLead = leadBreakdown[0];

  return (
    <div className="space-y-8 animate-fade-in font-sans -m-6 md:-m-8 p-6 md:p-8 min-h-full" style={{ background: 'linear-gradient(180deg, #F7EFE3 0%, #F3E8D8 100%)' }} data-testid="design-tracker-module-root">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-[#e2d3ba] pb-6">
        <div>
          <span className="text-[10px] font-mono uppercase tracking-widest text-[#9b7d54] block mb-1">
            Product R&D Ledger
          </span>
          <h1 className="text-3xl font-semibold font-arial text-slate-800 tracking-tight">
            Design & Development Tracker
          </h1>
          <p className="text-xs text-slate-500 mt-1 font-sans">
            Direct sync with Google Sheets for active SKU R&D, sampling statuses, and launch timelines.
          </p>
        </div>
        <div className={`px-4 py-2.5 rounded-xl flex items-center gap-2.5 text-xs font-semibold border ${connected ? 'bg-blue-50 text-blue-700 border-blue-100' : 'bg-slate-100 text-slate-400 border-slate-200'}`} data-testid="design-tracker-connection-badge">
          <FileSpreadsheet className="h-4.5 w-4.5" />
          <span>{connected ? `Synced ${syncedAt ? formatIST(syncedAt) : ''}` : 'Not connected'}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          platform="Google Sheets"
          title="Products Tracked"
          value={String(dataRows.length)}
          change="Product Plan tab"
          trend="neutral"
          insight={{ title: 'Product Plan', metric: 'Products Tracked', value: String(dataRows.length), aiInsight: `${dataRows.length} products are currently listed on the Product Plan tab.`, prediction: 'Trend requires more sync history to project.', nextSteps: ['Review the Recent Changes log below for what moved since the last sync.'] }}
          onHover={onHover}
        />
        <MetricCard
          platform="Design Lead"
          title="Busiest Lead"
          value={topLead ? topLead.name : '—'}
          change={topLead ? `${topLead.value} products` : ''}
          trend="neutral"
          insight={{ title: 'Lead Workload', metric: 'Busiest Lead', value: topLead ? `${topLead.name} (${topLead.value})` : '—', aiInsight: topLead ? `${topLead.name} currently owns the most products on the plan (${topLead.value} of ${dataRows.length}).` : 'No Lead data available yet.', prediction: 'Trend requires more sync history to project.', nextSteps: ['See the Lead workload chart below for the full breakdown.'] }}
          onHover={onHover}
        />
        <MetricCard
          platform="Launch Pipeline"
          title="Total SKUs Planned"
          value={totalSkus !== null ? String(totalSkus) : 'N/A'}
          change="Sum of 'No. of SKUs' col"
          trend="neutral"
          insight={{ title: 'SKU Volume', metric: 'Total SKUs Planned', value: totalSkus !== null ? String(totalSkus) : 'N/A', aiInsight: totalSkus !== null ? `${totalSkus} total SKUs planned across all tracked products.` : `The sheet's 'No. of SKUs' column is mostly blank — showing N/A rather than fabricating a total.`, prediction: 'Trend requires more sync history to project.', nextSteps: ['Fill in the No. of SKUs column in Google Sheets for a more complete rollup.'] }}
          onHover={onHover}
        />
        <MetricCard
          platform="Sheet Tabs"
          title="Tracker Tabs Synced"
          value={String(Object.keys(tabs).length)}
          change={Object.keys(tabs).join(', ').slice(0, 40)}
          trend="neutral"
          insight={{ title: 'Synced Tabs', metric: 'Tabs', value: String(Object.keys(tabs).length), aiInsight: `${Object.keys(tabs).length} tabs are synced from the Design Tracker sheet: ${Object.keys(tabs).join(', ')}.`, prediction: 'Trend requires more sync history to project.', nextSteps: ['Browse each tab directly in the live sheet viewer below.'] }}
          onHover={onHover}
        />
      </div>

      {(leadBreakdown.length > 0 || quarterBreakdown.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm space-y-4" data-testid="design-lead-chart">
            <h3 className="font-display font-semibold text-slate-700 text-sm tracking-wide uppercase">Products by Lead</h3>
            <div className="h-56 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={leadBreakdown} layout="vertical" margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
                  <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" stroke="#94a3b8" fontSize={10} tickLine={false} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" stroke="#94a3b8" fontSize={10} tickLine={false} width={80} />
                  <ChartTooltip contentStyle={{ background: '#ffffff', borderColor: '#e2e8f0', borderRadius: '12px' }} formatter={(v: any) => [`${v} products`, 'Count']} />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                    {leadBreakdown.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm space-y-4" data-testid="design-quarter-chart">
            <h3 className="font-display font-semibold text-slate-700 text-sm tracking-wide uppercase">Launch Quarter Distribution</h3>
            <div className="h-56 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={quarterBreakdown} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" stroke="#94a3b8" fontSize={10} tickLine={false} />
                  <YAxis stroke="#94a3b8" fontSize={10} tickLine={false} allowDecimals={false} />
                  <ChartTooltip contentStyle={{ background: '#ffffff', borderColor: '#e2e8f0', borderRadius: '12px' }} formatter={(v: any) => [`${v} products`, 'Count']} />
                  <Bar dataKey="value" fill="#1d4ed8" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      <RecentChangesPanel apiPath="/design-tracker/changes" label="Design Tracker" />

      <SheetDataViewer apiPath="/design-tracker" label="Design and Development Tracker" changesApiPath="/design-tracker/changes" />
    </div>
  );
}
